import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Trophy, Star, Crown } from "@phosphor-icons/react";

const URBAN_BG = "https://images.unsplash.com/photo-1638204997506-6dd11dd187de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMG5pZ2h0JTIwc2t5bGluZXxlbnwwfHx8fDE3ODE5NzA0NTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Leaderboard() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/leaderboard")
      .then((r) => setTracks(r.data))
      .catch((e) => console.error("Failed to load leaderboard", e))
      .finally(() => setLoading(false));
  }, []);

  const medal = (i) => {
    const colors = ["#E1FF00", "#B14AED", "#0038FF"];
    return colors[i] || "#52525B";
  };

  let content;
  if (loading) {
    content = <p className="text-[#52525B] font-mono text-sm">Loading rankings…</p>;
  } else if (tracks.length === 0) {
    content = (
      <div className="text-center py-20">
        <Trophy size={48} className="text-[#52525B] mx-auto mb-4" />
        <p className="font-head font-bold text-xl">No ranked tracks yet</p>
        <p className="text-[#A1A1AA] mt-1">Once tracks get reviewed, they'll show up here.</p>
      </div>
    );
  } else {
    content = (
      <div className="space-y-3" data-testid="leaderboard-list">
        {tracks.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            className="flex items-center gap-5 bg-[#0F0F13] border border-white/5 p-5 hover:border-white/15 transition-colors"
            style={i === 0 ? { boxShadow: "0 0 30px rgba(225,255,0,0.1)", borderColor: "rgba(225,255,0,0.3)" } : {}}
          >
            <div className="font-head font-black text-3xl w-12 text-center shrink-0 flex items-center justify-center" style={{ color: medal(i) }}>
              {i === 0 ? <Crown size={32} weight="fill" /> : String(i + 1).padStart(2, "0")}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-head font-bold text-lg tracking-tight truncate">{t.title}</p>
              <p className="text-[#A1A1AA] text-sm truncate">{t.artist_name} · <span className="text-[#B14AED]">{t.genre}</span></p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-head font-black text-2xl text-[#E1FF00] flex items-center gap-1 justify-end">
                <Star size={20} weight="fill" /> {t.avg_rating}
              </div>
              <div className="font-mono text-xs text-[#52525B]">{t.rating_count} reviews</div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <Navbar />

      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src={URBAN_BG} alt="" className="w-full h-full object-cover opacity-25 blur-sm" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 to-[#050505]" />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-10 py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E1FF00]/40 bg-[#E1FF00]/5 mb-5">
            <Trophy size={16} weight="fill" className="text-[#E1FF00]" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#E1FF00]">Top Rated</span>
          </div>
          <h1 className="font-head font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">The Leaderboard</h1>
          <p className="text-[#A1A1AA] text-lg mt-3">The highest-rated tracks the room has reviewed.</p>
        </div>
      </section>

      <main className="max-w-[1100px] mx-auto px-5 md:px-10 py-10">
        {content}
      </main>
    </div>
  );
}
